/**
 * Bringing a player in from a QR code, save picture or pasted link.
 *
 * Shared by the boot route (a scanned link opens the game with #save=) and
 * Settings, so both ask the same question and say the same things.
 */
import { decodeProfileCode } from '../../systems/profileCode';
import type { SaveManager } from '../../systems/SaveManager';
import { countOf } from '../../systems/saveTransfer';

export type ImportOutcome = 'imported' | 'cancelled' | 'invalid' | 'full';

export function confirmAndImportPlayer(save: SaveManager, code: string): ImportOutcome {
  const result = decodeProfileCode(code);
  if (!result.ok) {
    window.alert("That save code didn't work. It may have been copied only part way.");
    return 'invalid';
  }
  const { profile } = result;
  const target = save.profileImportSlot(profile.name);
  if (target === null) {
    window.alert(
      `All three player spots on this device are taken. Delete a player first, then try again to add ${profile.name}.`,
    );
    return 'full';
  }
  const summary = `${countOf(profile.totals.levelsCompleted, 'level')}, ${countOf(profile.totals.stars, 'star')}`;
  const question = target.replacing
    ? `Load ${profile.name}'s saved progress (${summary})?\n\nThis replaces ${profile.name} on this device. Other players are not changed.`
    : `Add ${profile.name} (${summary}) to this device?`;
  if (!window.confirm(question)) return 'cancelled';
  save.importProfile(profile, target.slot);
  return 'imported';
}
